import { type DeviceFragment, DeviceGeneralStatus, DeviceType } from '@cobliteam/api-lib/src/__generated__/graphql';

import { DeviceGeneralType, DeviceVehicleAssociationStatus, DisablerStatus, VehicleStatus } from '@cobliteam/common-types';
import type { History } from 'history';

const updateUrlSearchParam = (paramName: string, history: History, values?: string[] | string) => {
  const urlParams = new URLSearchParams(location.search);

  if (values && values.length > 0) {
    const paramValue = Array.isArray(values) ? values.join(',') : values;
    urlParams.set(paramName, paramValue);
  } else {
    urlParams.delete(paramName);
  }

  history.replace({ search: `?${urlParams.toString()}` });
};

const isAssociated = (device: DeviceFragment) => !!device.vehicle;

const getDeviceVehicleAssociationStatus = (device: DeviceFragment) => {
  const { vehicle, generalStatus } = device;
  if (vehicle?.id) {
    return DeviceVehicleAssociationStatus.ASSOCIATED;
  }

  if (generalStatus === DeviceGeneralStatus.LosingData) {
    return DeviceVehicleAssociationStatus.LOSING_DATA;
  }

  return DeviceVehicleAssociationStatus.NOT_ASSOCIATED;
};

const getDeviceVehicleStatus = (device: DeviceFragment, disablerStatus: DisablerStatus): VehicleStatus => {
  if (device.lastLocation && device.lastLocation.isPlugged === false) {
    return VehicleStatus.REMOVED;
  }
  if (device.type === DeviceType.Blocker && disablerStatus === DisablerStatus.Disabled) {
    return VehicleStatus.DISABLED;
  }
  if (device.type === DeviceType.Immobilizer) {
    switch (disablerStatus) {
      case DisablerStatus.Disabling:
      case DisablerStatus.Disabled:
        return VehicleStatus.DISABLED;
      case DisablerStatus.DisablePending:
        return VehicleStatus.DISABLE_PENDING;
      case DisablerStatus.EnablePending:
        return VehicleStatus.ENABLE_PENDING;
      default:
        break;
    }
  }
  if (device?.lastLocation?.ignitionOn) {
    return VehicleStatus.ON;
  }
  return VehicleStatus.OFF;
};

const getGeneralTypeEnum = (generalType: string | undefined) => DeviceGeneralType[generalType as keyof typeof DeviceGeneralType] || DeviceGeneralType.UNKNOWN;

const getDeviceStatus = (device: DeviceFragment) => {
  const { generalStatus } = device;
  if (
    generalStatus === DeviceGeneralStatus.Damaged ||
    generalStatus === DeviceGeneralStatus.LosingData ||
    generalStatus === DeviceGeneralStatus.Unavailable ||
    generalStatus === DeviceGeneralStatus.NoSufficientData
  ) {
    return 'critical';
  }
  if (generalStatus === DeviceGeneralStatus.Disconnected || generalStatus === DeviceGeneralStatus.NoSignal || generalStatus === DeviceGeneralStatus.InDevolution) {
    return 'check';
  }
  return 'healthy';
};

export { updateUrlSearchParam, isAssociated, getDeviceVehicleAssociationStatus, getDeviceVehicleStatus, getGeneralTypeEnum, getDeviceStatus };
